import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Spinner,
  Center,
  Icon,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { AlertTriangle, Clock, Flame } from 'lucide-react'
import { PasscodePrompt } from './components/PasscodePrompt'
import { BurnConfirmModal } from './components/BurnConfirmModal'
import { Editor } from '@/components/editor'
import { getManagementToken } from '@/lib/storage/local-storage'
import { useShareLoading } from './useShareLoading'

export function ViewSharePage() {
  const {
    state,
    metadata,
    decryptedContent,
    error,
    shareId,
    defaultViewMode,
    handleDecrypt,
    handleBurnConfirm,
    handleBurnCancel,
    handleEdit,
  } = useShareLoading()

  const cardBg = useColorModeValue('white', 'stone.800')

  if (state === 'loading') {
    return (
      <Center flex={1}>
        <Spinner size="xl" color="brand.500" />
      </Center>
    )
  }

  if (state === 'error') {
    return (
      <Center flex={1}>
        <Card maxW="md" mx="auto" bg={cardBg}>
          <CardBody>
            <VStack spacing={4} textAlign="center">
              <Icon as={AlertTriangle} boxSize={12} color="red.500" />
              <Heading size="md" fontFamily="heading">Share Not Found</Heading>
              <Text color="stone.500">{error}</Text>
            </VStack>
          </CardBody>
        </Card>
      </Center>
    )
  }

  if (state === 'expired') {
    return (
      <Center flex={1}>
        <Card maxW="md" mx="auto" bg={cardBg}>
          <CardBody>
            <VStack spacing={4} textAlign="center">
              <Icon as={Clock} boxSize={12} color="stone.400" />
              <Heading size="md" fontFamily="heading">Share Expired</Heading>
              <Text color="stone.500">
                This share has expired and is no longer available.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Center>
    )
  }

  if (state === 'consumed') {
    return (
      <Center flex={1}>
        <Card maxW="md" mx="auto" bg={cardBg}>
          <CardBody>
            <VStack spacing={4} textAlign="center">
              <Icon as={Flame} boxSize={12} color="orange.500" />
              <Heading size="md" fontFamily="heading">Share Consumed</Heading>
              <Text color="stone.500">
                This share was set to burn after reading and has already been viewed.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Center>
    )
  }

  if (state === 'burn-confirm') {
    return (
      <BurnConfirmModal
        isOpen
        onConfirm={handleBurnConfirm}
        onCancel={handleBurnCancel}
      />
    )
  }

  if (state === 'passcode') {
    return (
      <Center flex={1}>
        <Card maxW="md" mx="auto" bg={cardBg}>
          <CardHeader>
            <Heading size="md" textAlign="center">Enter Passcode</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <PasscodePrompt
              onSubmit={handleDecrypt}
              metadata={metadata}
            />
          </CardBody>
        </Card>
      </Center>
    )
  }

  if (state === 'viewing' && decryptedContent) {
    // Check if this is an editable share owned by the user
    const managementToken = shareId ? getManagementToken(shareId) : null
    const canEditInPlace = metadata?.isModifiable && managementToken

    return (
      <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minH={0}>
        <Editor
          initialContent={decryptedContent}
          initialFilename={metadata?.filename || 'untitled'}
          readOnly={!canEditInPlace}
          hideSidebar
          defaultFullscreen
          defaultViewMode={defaultViewMode}
          onEdit={canEditInPlace ? undefined : handleEdit}
          viewingShareIdProp={canEditInPlace ? shareId : undefined}
          viewingShareMetaProp={canEditInPlace
            ? { isModifiable: true, managementToken: managementToken ?? undefined, burnAfterRead: metadata?.burnAfterRead, expiresAt: metadata?.expiresAt }
            : { isModifiable: false, burnAfterRead: metadata?.burnAfterRead, expiresAt: metadata?.expiresAt }}
        />
      </Box>
    )
  }

  return null
}
